import { Button, CopyButton, FileButton, Group, Stack, Text, TextInput } from '@mantine/core'
import { notifications } from '@mantine/notifications'
import { IconCamera, IconCopy, IconFileCheck, IconLink, IconMessage, IconShare, IconSignature } from '@tabler/icons-react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { formatDate, formatDateTime } from '@tnn/core'
import { type BoxSubject, boxActions, boxOf, type ScreenAsk, type Signature, type SigningChannel, type SignLinkInfo } from '@tnn/domain'
import { currentLocale } from '@tnn/i18n'
import { ResponsiveDialog } from '@tnn/ui'
import { type ReactNode, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { api, isApiError } from '../api/client'
import { useCompany } from '../api/company'
import { queryKeys, type Rec } from '../api/resources'
import { signLink } from '../app/links'
import { useCan } from '../auth/session'
import { QrCode } from '../scan/QrCode'
import { uploadSignature, useSignatures } from './api'
import { type NewSignature, SignatureCapture } from './capture'
import { SignedBy } from './choice'
import { onLine } from './mark'
import classes from './signature.module.css'

/**
 * A document's signing box (`boxOf` in @tnn/domain): the line someone outside the company signs — a customer receiving
 * an accounting document, a sender accepting a bill's terms — shown signed (who, when, how), or the ways to have it
 * signed, the usual one first: here on this screen or one's portable screen (then a QR for them to keep a copy, but for
 * a bill, whose printout they take), a link
 * sent to them, or signed by hand on the printout with a photo of the page kept. Signed one way, the others close.
 */

export interface SigningBoxProps {
  subject: BoxSubject
  record: Rec
  /** The line, above it while unsigned: "ลูกค้าเซ็นรับ" */
  heading: string
  /** Above the signature once signed: "ลูกค้าเซ็นรับแล้ว" */
  signedCaption: string
  /** Under the heading while nothing is sent: what else may be done (the printed line signed by hand) */
  unsignedHint?: string
  /** The document as the signer knows it, in the link's message and on the QR: "ใบแจ้งหนี้" */
  document: string
  /** Who signs, filled in first: the bill's sender */
  signer?: string
  /** Signing here: what it is, above the pad; on the portable screen (by the name typed) */
  ask: { title: string; intro?: ReactNode; confirm?: string; screen: (name: string) => ScreenAsk }
  /** Open signing here at once (a bill just submitted, its sender still at the counter), then `onOpened` */
  openHere?: boolean
  onOpened?: () => void
}

const icons: Record<SigningChannel, typeof IconSignature> = { here: IconSignature, link: IconLink, paper: IconFileCheck }

export function SigningBox({
  subject,
  record,
  heading,
  signedCaption,
  unsignedHint,
  document,
  signer,
  ask,
  openHere,
  onOpened,
}: SigningBoxProps) {
  const { t, i18n } = useTranslation()
  const locale = currentLocale(i18n)
  const can = useCan()
  const client = useQueryClient()
  const id = String(record.id)
  const box = boxOf(subject)
  const signatures = useSignatures(subject, id)
  const signed = box && onLine(signatures.data, box.role)
  const allowed = !!box && can(boxActions[subject], subject, record)
  const channels = box?.channels ?? []
  const linkKey = ['signLink', subject, id]
  const link = useQuery({
    queryKey: linkKey,
    queryFn: () => api.get<{ item: SignLinkInfo | null }>(`/signatures/links/${subject}/${id}`).then((r) => r.item),
    enabled: signatures.isSuccess && !signed && channels.includes('link'),
  })
  const open = link.data?.status === 'open' ? link.data : null
  const [dialog, setDialog] = useState<SigningChannel | null>(null)
  // Signed here: the link they keep it on, its QR shown over the signed box
  const [kept, setKept] = useState<SignLinkInfo | null>(null)
  const fail = (error: Error) => notifications.show({ color: 'danger', message: error.message })
  const send = useMutation({
    mutationFn: () => api.post<{ item: SignLinkInfo }>('/signatures/links', { subject, recordId: id }).then((r) => r.item),
    onSuccess: (item) => {
      client.setQueryData(linkKey, item)
      client.invalidateQueries({ queryKey: queryKeys.resource(subject) })
      setDialog('link')
    },
    onError: fail,
  })

  // Right after it is submitted here, the signer still in front of staff
  const ready = signatures.isSuccess && allowed && !signed && channels.includes('here')
  useEffect(() => {
    if (!openHere || !signatures.isSuccess) return
    if (ready) setDialog('here')
    onOpened?.()
  }, [openHere, ready, signatures.isSuccess, onOpened])

  if (!box) return null
  const number = String(record.number ?? '')
  /** Signed one way: the signature beside it, the list's column and the link as they now stand */
  const took = (item: Signature, keep?: SignLinkInfo | null) => {
    client.setQueryData(['signatures', subject, id], (items: Signature[] | undefined) => [...(items ?? []), item])
    client.invalidateQueries({ queryKey: linkKey })
    client.invalidateQueries({ queryKey: queryKeys.resource(subject) })
    notifications.show({ color: 'primary', message: t('signing.saved') })
    setDialog(null)
    if (keep?.token) setKept(keep)
  }
  if (signed) {
    return (
      <>
        <SignedBy signature={signed} caption={signedCaption} />
        {kept && <KeepDialog link={kept} document={document} number={number} onClose={() => setKept(null)} />}
      </>
    )
  }

  const status = open
    ? [
        t('signing.waiting'),
        open.viewedAt ? t('signing.viewed', { at: formatDateTime(open.viewedAt, { locale, style: 'short' }) }) : t('signing.notViewed'),
      ].join(' · ')
    : unsignedHint
  const choose = (channel: SigningChannel) => (channel === 'link' && !open ? send.mutate() : setDialog(channel))
  const labelOf = (channel: SigningChannel) => (channel === 'link' && open ? t('signing.showLink') : t(`signing.${channel}`))
  const [first, ...rest] = allowed ? channels : []

  return (
    <Stack gap={6}>
      <Text size="sm" fw={500}>
        {heading}
      </Text>
      {status && (
        <Text size="xs" c="dimmed">
          {status}
        </Text>
      )}
      {first && (
        <Stack gap={4}>
          <ChannelButton
            channel={first}
            label={labelOf(first)}
            primary
            loading={first === 'link' && send.isPending}
            onClick={() => choose(first)}
          />
          <Group gap={4} grow>
            {rest.map((channel) => (
              <ChannelButton
                key={channel}
                channel={channel}
                label={labelOf(channel)}
                loading={channel === 'link' && send.isPending}
                onClick={() => choose(channel)}
              />
            ))}
          </Group>
        </Stack>
      )}
      <HereDialog
        opened={dialog === 'here'}
        onClose={() => setDialog(null)}
        subject={subject}
        id={id}
        ask={ask}
        signer={signer}
        onSigned={took}
      />
      {open?.token && (
        <LinkDialog
          opened={dialog === 'link'}
          onClose={() => setDialog(null)}
          link={open}
          document={document}
          number={number}
          onRevoked={() => {
            client.setQueryData(linkKey, null)
            client.invalidateQueries({ queryKey: queryKeys.resource(subject) })
            setDialog(null)
          }}
        />
      )}
      <PaperDialog
        opened={dialog === 'paper'}
        onClose={() => setDialog(null)}
        subject={subject}
        id={id}
        title={ask.title}
        signer={signer}
        onSigned={took}
      />
    </Stack>
  )
}

function ChannelButton({
  channel,
  label,
  primary,
  loading,
  onClick,
}: {
  channel: SigningChannel
  label: string
  primary?: boolean
  loading?: boolean
  onClick: () => void
}) {
  const Icon = icons[channel]
  return (
    <Button
      variant={primary ? 'light' : 'subtle'}
      size={primary ? 'sm' : 'compact-sm'}
      leftSection={<Icon size={primary ? 18 : 16} />}
      loading={loading}
      onClick={onClick}
    >
      {label}
    </Button>
  )
}

/** Who signs, as printed under it: their name (the party's filled in first) and title */
function useSigner(opened: boolean, signer?: string) {
  const [name, setName] = useState('')
  const [title, setTitle] = useState('')
  const [missing, setMissing] = useState(false)
  // Each opening starts from the party's name
  useEffect(() => {
    if (!opened) return
    setName(signer ?? '')
    setTitle('')
    setMissing(false)
  }, [opened, signer])
  return { name, title, missing, setMissing, setName, setTitle }
}

function SignerFields({ who }: { who: ReturnType<typeof useSigner> }) {
  const { t } = useTranslation()
  return (
    <Group grow gap="sm" align="flex-start">
      <TextInput
        label={t('signing.name')}
        withAsterisk
        value={who.name}
        error={who.missing && !who.name.trim() ? t('form.required') : undefined}
        onChange={(e) => who.setName(e.currentTarget.value)}
      />
      <TextInput label={t('signing.title')} value={who.title} onChange={(e) => who.setTitle(e.currentTarget.value)} />
    </Group>
  )
}

/** Signed here, drawn on this screen or the portable screen (`onSigned` with the link they keep it on) */
function HereDialog({
  opened,
  onClose,
  subject,
  id,
  ask,
  signer,
  onSigned,
}: {
  opened: boolean
  onClose: () => void
  subject: BoxSubject
  id: string
  ask: SigningBoxProps['ask']
  signer?: string
  onSigned: (item: Signature, keep: SignLinkInfo | null) => void
}) {
  const { t } = useTranslation()
  const who = useSigner(opened, signer)
  const [signature, setSignature] = useState<NewSignature | null>(null)
  const [saving, setSaving] = useState(false)
  useEffect(() => {
    if (opened) setSignature(null)
  }, [opened])
  const confirm = async () => {
    if (!who.name.trim() || !signature) return who.setMissing(true)
    setSaving(true)
    try {
      const fileId = await uploadSignature(signature)
      const signed = await api.post<{ item: Signature; link: SignLinkInfo | null }>(`/signatures/${subject}/${id}/here`, {
        name: who.name.trim(),
        title: who.title.trim() || null,
        signature: { fileId, method: signature.method },
      })
      onSigned(signed.item, signed.link)
    } catch (error) {
      notifications.show({ color: 'danger', message: isApiError(error) ? error.message : t('errors.generic') })
    } finally {
      setSaving(false)
    }
  }
  // Before a name is typed, the statement keeps its place for it
  const screen = ask.screen(who.name.trim() || '…')
  return (
    <ResponsiveDialog
      opened={opened}
      onClose={onClose}
      title={ask.title}
      size="md"
      footer={
        <>
          <Button variant="subtle" onClick={onClose}>
            {t('actions.cancel')}
          </Button>
          <Button loading={saving} onClick={confirm}>
            {ask.confirm ?? t('signing.save')}
          </Button>
        </>
      }
    >
      <Stack gap="md">
        {ask.intro}
        <SignerFields who={who} />
        {screen.statement && (
          <Text size="sm" fw={500}>
            {screen.statement}
          </Text>
        )}
        <Stack gap={6}>
          {opened && <SignatureCapture onChange={setSignature} methods={['drawn']} screen={screen} />}
          {who.missing && !signature && (
            <Text size="sm" c="var(--mantine-color-error)">
              {t('signing.needSignature')}
            </Text>
          )}
        </Stack>
      </Stack>
    </ResponsiveDialog>
  )
}

/** Just signed here: a QR for the signer to keep the signed document on their phone */
function KeepDialog({ link, document, number, onClose }: { link: SignLinkInfo; document: string; number: string; onClose: () => void }) {
  const { t, i18n } = useTranslation()
  return (
    <ResponsiveDialog
      opened
      onClose={onClose}
      title={t('signing.keepTitle')}
      size="sm"
      footer={<Button onClick={onClose}>{t('signing.done')}</Button>}
    >
      <Stack align="center" gap="md">
        <QrCode value={signLink(String(link.token))} size={208} label={document} detail={number} />
        <Text size="sm" c="dimmed" ta="center">
          {t('signing.keepHint', { date: formatDate(link.expiresAt, { locale: currentLocale(i18n), style: 'long' }) })}
        </Text>
      </Stack>
    </ResponsiveDialog>
  )
}

/** The open link: its QR to scan at the counter, copied alone or in a message for LINE, shared from a phone, withdrawn */
function LinkDialog({
  opened,
  onClose,
  link,
  document,
  number,
  onRevoked,
}: {
  opened: boolean
  onClose: () => void
  link: SignLinkInfo
  document: string
  number: string
  onRevoked: () => void
}) {
  const { t, i18n } = useTranslation()
  const company = useCompany().data
  const url = signLink(String(link.token))
  const message = t('signing.message', { company: company?.name ?? '', document, number, url })
  const shares = typeof navigator !== 'undefined' && 'share' in navigator
  const revoke = useMutation({
    mutationFn: () => api.post(`/signatures/links/${link.id}/revoke`),
    onSuccess: () => {
      notifications.show({ message: t('signing.revoked') })
      onRevoked()
    },
    onError: (error: Error) => notifications.show({ color: 'danger', message: error.message }),
  })
  return (
    <ResponsiveDialog opened={opened} onClose={onClose} title={t('signing.linkTitle')} size="sm">
      <Stack align="center" gap="md">
        <QrCode value={url} size={208} label={document} detail={number} />
        <Text size="sm" c="dimmed" ta="center">
          {t('signing.linkIntro')}
        </Text>
        <Group grow gap="sm" w="100%">
          <CopyButton value={url}>
            {({ copied, copy }) => (
              <Button variant="light" leftSection={<IconCopy size={18} />} onClick={copy}>
                {copied ? t('signing.copied') : t('signing.copy')}
              </Button>
            )}
          </CopyButton>
          <CopyButton value={message}>
            {({ copied, copy }) => (
              <Button leftSection={<IconMessage size={18} />} onClick={copy}>
                {copied ? t('signing.copied') : t('signing.copyMessage')}
              </Button>
            )}
          </CopyButton>
        </Group>
        {shares && (
          <Button
            variant="light"
            fullWidth
            leftSection={<IconShare size={18} />}
            onClick={() => navigator.share({ text: message }).catch(() => undefined)}
          >
            {t('signing.share')}
          </Button>
        )}
        <Text size="xs" c="dimmed" ta="center">
          {t('signing.expires', { date: formatDate(link.expiresAt, { locale: currentLocale(i18n), style: 'long' }) })}
        </Text>
        <Button variant="subtle" color="danger" size="compact-sm" loading={revoke.isPending} onClick={() => revoke.mutate()}>
          {t('signing.revoke')}
        </Button>
      </Stack>
    </ResponsiveDialog>
  )
}

/** Signed by hand on the printout: who, and a photo of the page kept as the proof */
function PaperDialog({
  opened,
  onClose,
  subject,
  id,
  title,
  signer,
  onSigned,
}: {
  opened: boolean
  onClose: () => void
  subject: BoxSubject
  id: string
  title: string
  signer?: string
  onSigned: (item: Signature) => void
}) {
  const { t } = useTranslation()
  const who = useSigner(opened, signer)
  const [photo, setPhoto] = useState<{ id: string; url: string } | null>(null)
  const [taking, setTaking] = useState(false)
  const [saving, setSaving] = useState(false)
  useEffect(() => {
    if (opened) setPhoto(null)
  }, [opened])
  useEffect(() => () => (photo ? URL.revokeObjectURL(photo.url) : undefined), [photo])
  const take = async (file: File | null) => {
    if (!file) return
    setTaking(true)
    try {
      const stored = await api.upload(file)
      setPhoto({ id: stored.id, url: URL.createObjectURL(file) })
    } catch (error) {
      notifications.show({ color: 'danger', message: error instanceof Error ? error.message : String(error) })
    } finally {
      setTaking(false)
    }
  }
  const confirm = async () => {
    if (!who.name.trim() || !photo) return who.setMissing(true)
    setSaving(true)
    try {
      const { item } = await api.post<{ item: Signature }>(`/signatures/${subject}/${id}/paper`, {
        name: who.name.trim(),
        title: who.title.trim() || null,
        photoFileId: photo.id,
      })
      onSigned(item)
    } catch (error) {
      notifications.show({ color: 'danger', message: isApiError(error) ? error.message : t('errors.generic') })
    } finally {
      setSaving(false)
    }
  }
  return (
    <ResponsiveDialog
      opened={opened}
      onClose={onClose}
      title={`${title} · ${t('signing.paper')}`}
      size="sm"
      footer={
        <>
          <Button variant="subtle" onClick={onClose}>
            {t('actions.cancel')}
          </Button>
          <Button loading={saving} disabled={taking} onClick={confirm}>
            {t('signing.save')}
          </Button>
        </>
      }
    >
      <Stack gap="md">
        <SignerFields who={who} />
        <Stack gap={6}>
          <FileButton onChange={take} accept="image/*" capture="environment">
            {(props) =>
              photo ? (
                <Group gap="sm" wrap="nowrap" align="flex-end">
                  <span className={classes.pagePhoto}>
                    <img src={photo.url} alt={t('signing.pagePhoto')} />
                  </span>
                  <Button {...props} variant="subtle" leftSection={<IconCamera size={18} />} loading={taking}>
                    {t('signing.retake')}
                  </Button>
                </Group>
              ) : (
                <Button {...props} variant="light" size="md" leftSection={<IconCamera size={20} />} loading={taking}>
                  {t('signing.photo')}
                </Button>
              )
            }
          </FileButton>
          <Text size="xs" c={who.missing && !photo ? 'var(--mantine-color-error)' : 'dimmed'}>
            {who.missing && !photo ? t('signing.needPhoto') : t('signing.photoHint')}
          </Text>
        </Stack>
      </Stack>
    </ResponsiveDialog>
  )
}
